/**
 * The validation thread's composer — text plus attachments:
 *
 *   • loan DOCUMENTS — picked from the loan's document inventory (search, tap
 *     to attach). Sent as `attachments[]` and, because the server's turn reads
 *     `message` text today, also folded into the message as an "Attached:"
 *     trailer so the assistant (grounded in the same inventory by name) knows
 *     which files are being discussed. Chips preview on click.
 *   • IMAGES — paste, drop, or pick; thumbnails in the tray; sent as
 *     `images[]` (base64). Only offered when the thread reports
 *     `assistant.acceptsImages` — the current model turn is text-only and
 *     would silently drop them, so the affordance stays hidden until the
 *     backend advertises vision support.
 *
 * `onSend({ message, attachments, images, text })` — `text` is the folded
 * message the server receives; `message` the user's own words.
 */
import { useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { isDocHidden } from "../../../_src_shared/utils/functions/isDocHidden";
import { useDispatch } from "react-redux";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import AttachFileOutlined from "@mui/icons-material/AttachFileOutlined";
import CloseRounded from "@mui/icons-material/CloseRounded";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import ImageOutlined from "@mui/icons-material/ImageOutlined";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import SendOutlined from "@mui/icons-material/SendOutlined";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { docDisplayName, FormNote } from "../../../components/forms/FormPrimitives";
import { filePreviewSetData } from "../../../redux/reducers/filePreviewReducer";
import { FORM, formSx, px } from "../../../theme/formStyles";

const MAX_MESSAGE = 8000;
const MAX_IMAGES = 4;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

/** The message the server receives: the user's words + the attached-document trailer. */
export const foldMessage = (message, attachments) => {
  const body = (message || "").trim();
  if (!attachments?.length) return body;
  const trailer = `Attached: ${attachments.map((a) => `${a.fileName}${a.docType ? ` (${a.docType})` : ""}`).join(", ")}`;
  return body ? `${body}\n\n${trailer}` : trailer;
};

const readAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ""));
    r.onerror = reject;
    r.readAsDataURL(file);
  });

/* ─────────────────────────── document picker ───────────────────────────── */

const DocumentPicker = ({ anchorEl, open, onClose, docs, attachedIds, onPick }) => {
  const [q, setQ] = useState("");
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    // Hidden documents are never offered as attachments.
    return docs.filter((d) => !isDocHidden(d) && (!needle || `${docDisplayName(d)} ${d.docType || ""} ${d.docGroup || ""}`.toLowerCase().includes(needle))).slice(0, 60);
  }, [docs, q]);
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "left" }}
      transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      PaperProps={{ sx: { width: 360, borderRadius: px(FORM.radius.control), border: `1px solid ${FORM.color.divider}`, boxShadow: "0 8px 28px rgba(15,23,42,.14)" } }}
    >
      <Box sx={{ p: px(FORM.s.s3), borderBottom: `1px solid ${FORM.color.divider}` }}>
        <TextField
          autoFocus
          size="small"
          fullWidth
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search loan documents…"
          sx={formSx.input}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined sx={{ fontSize: 18 }} /></InputAdornment> }}
        />
      </Box>
      <Box sx={{ maxHeight: 280, overflowY: "auto" }}>
        {rows.length ? (
          rows.map((d) => {
            const on = attachedIds.has(String(d._id));
            return (
              <Box
                key={d._id}
                component="button"
                type="button"
                onClick={() => onPick(d)}
                sx={{ ...formSx.listRow(on), width: "100%", minHeight: 40, border: 0, textAlign: "left", fontFamily: "inherit", "& + &": { borderTop: `1px solid ${FORM.color.divider}` } }}
              >
                <DescriptionOutlined sx={{ fontSize: 16, color: FORM.color.ink4, flexShrink: 0 }} />
                <Txt sx={{ ...formSx.listRowName, fontSize: FORM.type.label[0] }}>{docDisplayName(d)}</Txt>
                <Txt sx={formSx.listRowMeta}>{on ? "Attached" : d.docType || d.docGroup || ""}</Txt>
              </Box>
            );
          })
        ) : (
          <Txt sx={{ ...formSx.helper, display: "block", p: px(FORM.s.s4) }}>{docs.length ? "No documents match." : "No loan documents yet."}</Txt>
        )}
      </Box>
    </Popover>
  );
};

DocumentPicker.propTypes = {
  anchorEl: PropTypes.any,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  docs: PropTypes.array,
  attachedIds: PropTypes.instanceOf(Set),
  onPick: PropTypes.func,
};

/* ─────────────────────────────── composer ──────────────────────────────── */

const ExperienceChatComposer = ({ docs, acceptsImages = false, disabled, onSend }) => {
  const dispatch = useDispatch();
  const [draft, setDraft] = useState("");
  const [attachments, setAttachments] = useState([]); // [{ loanDocumentId, fileName, docType }]
  const [images, setImages] = useState([]); // [{ id, fileName, mimeType, dataUrl, bytes }]
  const [pickerAnchor, setPickerAnchor] = useState(null);
  const [imageError, setImageError] = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  const docsById = useMemo(() => new Map((docs || []).map((d) => [String(d?._id), d])), [docs]);
  const attachedIds = useMemo(() => new Set(attachments.map((a) => String(a.loanDocumentId))), [attachments]);

  const toggleDoc = (d) =>
    setAttachments((cur) =>
      cur.some((a) => String(a.loanDocumentId) === String(d._id))
        ? cur.filter((a) => String(a.loanDocumentId) !== String(d._id))
        : [...cur, { loanDocumentId: String(d._id), fileName: docDisplayName(d), docType: d.docType || d.docGroup || null }],
    );

  const addImages = async (files) => {
    if (!acceptsImages) return;
    const list = Array.from(files || []).filter((f) => f.type?.startsWith("image/"));
    if (!list.length) return;
    const room = MAX_IMAGES - images.length;
    if (room <= 0) {
      setImageError(`Up to ${MAX_IMAGES} images per message.`);
      return;
    }
    const tooBig = list.find((f) => f.size > MAX_IMAGE_BYTES);
    if (tooBig) setImageError(`${tooBig.name} is over 5 MB — pick a smaller image.`);
    const ok = list.filter((f) => f.size <= MAX_IMAGE_BYTES).slice(0, room);
    const read = await Promise.all(ok.map(async (f) => ({ id: `${f.name}-${f.size}-${Date.now()}`, fileName: f.name, mimeType: f.type, dataUrl: await readAsDataUrl(f), bytes: f.size })));
    setImages((cur) => [...cur, ...read]);
    if (!tooBig) setImageError(null);
  };

  const canSend = !disabled && (draft.trim().length > 0 || attachments.length > 0 || images.length > 0);

  const send = () => {
    if (!canSend) return;
    const message = draft.trim();
    const text = foldMessage(message, attachments).slice(0, MAX_MESSAGE);
    onSend({
      message,
      text,
      attachments,
      images: images.map((im) => ({ fileName: im.fileName, mimeType: im.mimeType, dataBase64: im.dataUrl.split(",")[1] || "" , dataUrl: im.dataUrl })),
    });
    setDraft("");
    setAttachments([]);
    setImages([]);
    setImageError(null);
  };

  const hasTray = attachments.length > 0 || images.length > 0;

  return (
    <Box
      onDragOver={(e) => {
        if (!acceptsImages) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        if (!acceptsImages) return;
        e.preventDefault();
        setDragging(false);
        addImages(e.dataTransfer?.files);
      }}
      sx={{
        border: `1px ${dragging ? "dashed" : "solid"} ${dragging ? FORM.color.accent : FORM.color.border}`,
        borderRadius: px(FORM.radius.control),
        bgcolor: FORM.color.surface,
        transition: "border-color .12s",
        "&:focus-within": { borderColor: FORM.color.accent },
      }}
    >
      {hasTray ? (
        <Flx ac g={0.75} wrap sx={{ px: px(FORM.s.s3), pt: px(FORM.s.s3) }}>
          {attachments.map((a) => {
            const doc = docsById.get(String(a.loanDocumentId));
            return (
              <Chip
                key={a.loanDocumentId}
                size="small"
                icon={<DescriptionOutlined sx={{ fontSize: 14 }} />}
                label={a.fileName}
                // A displayed file is always previewable.
                onClick={doc ? () => dispatch(filePreviewSetData(doc)) : undefined}
                onDelete={() => setAttachments((cur) => cur.filter((x) => x.loanDocumentId !== a.loanDocumentId))}
                sx={{ ...formSx.meta, height: 24, maxWidth: 220, bgcolor: FORM.color.surface2, border: `1px solid ${FORM.color.divider}` }}
              />
            );
          })}
          {images.map((im) => (
            <Box key={im.id} sx={{ position: "relative", width: 48, height: 48, borderRadius: px(6), overflow: "hidden", border: `1px solid ${FORM.color.divider}`, flexShrink: 0 }}>
              <Box component="img" src={im.dataUrl} alt={im.fileName} title={im.fileName} sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              <IconButton
                size="small"
                aria-label={`Remove ${im.fileName}`}
                onClick={() => setImages((cur) => cur.filter((x) => x.id !== im.id))}
                sx={{ position: "absolute", top: 1, right: 1, p: px(1), bgcolor: "rgba(15,23,42,.65)", color: "#fff", "&:hover": { bgcolor: "rgba(15,23,42,.85)" } }}
              >
                <CloseRounded sx={{ fontSize: 12 }} />
              </IconButton>
            </Box>
          ))}
        </Flx>
      ) : null}

      <Flx g={0.5} sx={{ alignItems: "flex-start", px: px(6), py: px(6) }}>
        <Tooltip title="Attach a loan document">
          <span>
            <IconButton size="small" onClick={(e) => setPickerAnchor(e.currentTarget)} disabled={disabled} aria-label="Attach a loan document" sx={{ color: FORM.color.ink3 }}>
              <AttachFileOutlined sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
        {acceptsImages ? (
          <Tooltip title="Attach an image (or paste / drop one)">
            <span>
              <IconButton size="small" onClick={() => fileInputRef.current?.click()} disabled={disabled || images.length >= MAX_IMAGES} aria-label="Attach an image" sx={{ color: FORM.color.ink3 }}>
                <ImageOutlined sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
        ) : null}
        <TextField
          size="small"
          fullWidth
          multiline
          maxRows={5}
          variant="standard"
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, MAX_MESSAGE))}
          onPaste={(e) => {
            const files = Array.from(e.clipboardData?.items || [])
              .filter((it) => it.kind === "file" && it.type?.startsWith("image/"))
              .map((it) => it.getAsFile())
              .filter(Boolean);
            if (files.length && acceptsImages) {
              e.preventDefault();
              addImages(files);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder={attachments.length ? "Add a note about the attached documents…" : "Which candidates still need proof, or a parent? Why was a row verified?"}
          disabled={disabled}
          InputProps={{
            disableUnderline: true,
            sx: {
              ...formSx.helper,
              color: FORM.color.ink,
              mt: 0,
              px: px(4),
              py: px(7),
              "& textarea::placeholder": { color: FORM.color.ink4, opacity: 1 },
            },
          }}
        />
        <Button size="small" onClick={send} disabled={!canSend} aria-label="Send" sx={{ minWidth: 0, px: px(10), height: 30, borderRadius: px(6), mt: px(1) }}>
          <SendOutlined sx={{ fontSize: 16 }} />
        </Button>
      </Flx>

      {imageError ? (
        <Box sx={{ px: px(FORM.s.s3), pb: px(FORM.s.s2) }}>
          <FormNote sx={{ py: px(6) }}>{imageError}</FormNote>
        </Box>
      ) : null}

      <input ref={fileInputRef} type="file" accept="image/*" multiple hidden onChange={(e) => { addImages(e.target.files); e.target.value = ""; }} />

      <DocumentPicker
        anchorEl={pickerAnchor}
        open={!!pickerAnchor}
        onClose={() => setPickerAnchor(null)}
        docs={docs || []}
        attachedIds={attachedIds}
        onPick={(d) => toggleDoc(d)}
      />
    </Box>
  );
};

ExperienceChatComposer.propTypes = {
  docs: PropTypes.array,
  acceptsImages: PropTypes.bool,
  disabled: PropTypes.bool,
  onSend: PropTypes.func,
};

export default ExperienceChatComposer;
